import { IAccountObservation } from './account';
import { ProductStatus } from './record-status';

export interface IProduct {
  DateTimeCreated: string;
  DateTimeModified: string;
  Description: string;
  DisplayOrder: number;
  FormId: string;
  Mnemonic: string;
  ProductGroupId: number;
  ProductGroupName?: string;
  ProductId: number;
  ProductName: string;
  ProductNumber: number;
  RecordStatus?: ProductStatus;
  SubProducts?: string;
  IsDefaultProduct?: boolean;
  UserIdCreated: number;
  UserIdModified: number;
  ProductType: number;
  ProductTypeText?: string;
  CounterpartyType: number;
  CounterpartyText?: string;
  Vintages?: string[];
  isChecked?: boolean;
  isDisabled?: boolean;
  TabId?: number;
}

export interface IProductGroup {
  ProductGroupId: number;
  GroupName: string;
  Description: string;
  IsActive: boolean;
  isChecked?: boolean;
  isDisabled?: boolean;
}

export interface IProductSetupLocalStorage {
  ProductSetups: IProduct[];
}

export interface IProductTag {
  Value: string;
  Key: number;
  Group?: string;
}

export const UNGROUPED = 'Un-grouped';
export const TAGS = {
  DEFAULT_TAG: '<default>',
  NULL_TAG: '<null>'
};

export class ProductFactory {
  public static createProduct(): IProduct {
    return {
      Description: null,
      DisplayOrder: null,
      Mnemonic: null,
      ProductGroupId: null,
      ProductId: null,
      ProductName: null,
      ProductNumber: null,
      SubProducts: null,
      RecordStatus: ProductStatus.Active,
      FormId: 'New product',
      ProductType: null,
      CounterpartyType: null,
      DateTimeCreated: null,
      DateTimeModified: null,
      UserIdCreated: -1,
      UserIdModified: null
    };
  }
}

export interface IProductConcentrationDetails {
  TotalAvgBalance: number;
  ProductId: number;
  ProductName: string;
  ObservationDate: Date;
  Top10Accounts: IAccountObservation[];
  Top10AvgBalance: number;
  NoData?: boolean;
}

export interface IProductObservationDates {
  ProductId: number;
  FirstObservationDate: Date;
  LastObservationDate: Date;
}

export interface IProductMenOption {
  type?: string;
  id?: number;
  path?: string;
  label?: string;
  disabled?: boolean;
}
export const allProductsItem: IProduct = {
  ...ProductFactory.createProduct(),
  DisplayOrder: 0,
  ProductGroupName: 'ALL',
  ProductName: 'All Products'
};

export function sortProducts(products: IProduct[] = null, productGroups: IProductGroup[] = null): void {
  if (productGroups && products) {
    for (const product of products) {
      const group = productGroups.find(({ ProductGroupId }) => ProductGroupId === product.ProductGroupId);
      product.ProductGroupName = group ? group.GroupName : UNGROUPED;
    }
  }

  if (products) {
    applyProductSortingRules(products);
  }
}

/**
 * Products will be sorted by:
 *   1. Tab Id
 *   2. Group Name
 *   3. Product Display Order
 *   4. Product Number (if display orders are equal)
 *   5. Product Name
 */
export const applyProductSortingRules = (products: IProduct[]) => {
  products
    // Case insensitive alphabetical order
    .sort((p1, p2) => (`${p1.ProductName || ''}`.toLocaleLowerCase() < `${p2.ProductName || ''}`.toLocaleLowerCase() ? -1 : 0))
    .sort((p1, p2) => p1.ProductNumber - p2.ProductNumber)
    .sort((p1, p2) => p1.DisplayOrder - p2.DisplayOrder)
    .sort((p1, p2) => (`${p1.ProductGroupName || ''}`.toLocaleLowerCase() < `${p2.ProductGroupName || ''}`.toLocaleLowerCase() ? -1 : 0))
    .sort((p1, p2) => p1.TabId - p2.TabId);
  return products;
};

export function sortProductGroups(productGroups: IProductGroup[]) {
  productGroups.sort(productGroupSorting);
}

export const productGroupSorting = (p1: IProductGroup, p2: IProductGroup) =>
  `${p1.GroupName || ''}`.toLocaleLowerCase() < `${p2.GroupName || ''}`.toLocaleLowerCase() ? -1 : 0;

export interface IProductTab {
  Name: string;
  Id: number;
  IsPublished: boolean;
  Products: ISetUpProduct[];
  ProductsDB?: ISetUpProduct[];
  DateTimeCreated: string;
  DateTimeModified: string;
  ToBeDeleted?: boolean;
  type?: string;
}

export interface ISetUpProduct extends IProduct {
  EditMode?: boolean;
  ToBeDeleted?: boolean;
  ToBeDeactivated?: boolean;
  ToBeReactivated?: boolean;
  Pristine?: boolean;
  TabName?: string;
}
